import {
  AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output,
  QueryList, ViewChild, ViewChildren
} from '@angular/core';
import {NgClass, NgFor, NgIf, NgTemplateOutlet} from '@angular/common';
import {Subscription} from 'rxjs';
import {CallParticipantsListComponent} from '@features/calls/call-participants-list/call-participants-list.component';
import {CallParticipants} from '@features/calls/call-participants';
import {CallPhoneInvite} from '@features/calls/call-media.port';
import {PhoneInviteDialogComponent} from '@shared/components/phone-invite-dialog/phone-invite-dialog.component';
import {VideoSlotLayout, VideoSlotReconciler} from '@shared/video-slots/video-slot-reconciler';
import {
  COMPACT_CALL_ICONS, CompactCallMode, CompactCallOption, CompactCallPerson, CompactCallRenderer, CompactCallSize, CompactCallViewModel
} from './compact-call-view.model';

/** One page of the thumbnails. */
export interface CompactCallPage {
  tiles: CompactCallPerson[];
  /** The page shown, from 0; kept inside the pages there are. */
  page: number;
  pages: number;
}

/** A panel the options (⋯) menu opens. */
type CompactCallPanel = 'participants' | 'devices' | 'phone';

/**
 * The compact call view (ITP-E-185-01, D-09/D-11): one presentational component for a call inside ITP, in two modes. As
 * a `card` it is Meet's picture-in-picture — the dark stage with the speaker's video (or initials) and their name
 * bottom-left, the others as thumbnails a page at a time, a shared screen in a canvas of its own (Meet's), and a footer of
 * square icon buttons: Mute, Camera, Share, ⋯ and the red End call. As a `strip` it is one slim row: the speaker's
 * thumbnail in a video call, the name and timer, Mute, Camera, ⋯ and End call, and Expand where the host offers it.
 *
 * ⋯ holds the rest, each only when it is this side's to do: Participants (the group's list with where everyone stands),
 * Devices (microphone and speaker), Share screen (in the strip), Record (the host), Open in Meet ↗ (the card's), and the
 * host's End call for everyone behind a confirm.
 *
 * ITP-E-198-01: and Add by phone, where the host says this user may and the call's account dials out — a small dialog of the
 * country, the number and the name. Call hands the invite to the host and opens Participants, where the phone person reads
 * Calling… then Ringing…, with Cancel, and Hang up once they answered.
 *
 * ITP-E-191-03: the strip is carbon's in-call mini bar — a pill on the bar's inverted ground (dark on the light theme,
 * light under `body.dark-theme`), the 28px face of the other person or the group (a video call keeps the speaker's
 * thumbnail instead), the name over "mm:ss · N in call", the controls as 36px circles, and a tertiary Open call that opens
 * the call in Meet — so ⋯ on the strip no longer repeats Open in Meet.
 *
 * It never reads the call service and never owns a media client: the host hands it a view-model and a renderer, and the
 * renderer draws the call's video into the slots the view gives it. Whenever the layout moves someone's video, the view
 * drops it where it was and draws it where it now goes. The host puts its own header on the card (`[compactCallHeader]`)
 * and its own handle on the strip (`[compactCallTitle]`), so it keeps dragging and sizing.
 *
 * The shared screen's canvas is the one exception to slots that come and go (ITP-E-189-07): there is one for the view's
 * life, mounted from the start and hidden while nobody else shares, as Meet keeps `#participant-screen-share-canvas` —
 * the SDK takes a canvas over and goes on drawing into the one it first got, so a canvas made again under `*ngIf` is the likeliest reason the stage stayed
 * blank. A sharer, size or mode change keeps it; the renderer is handed it once per sharer.
 */
@Component({
  selector: 'app-compact-call-view',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, NgTemplateOutlet, CallParticipantsListComponent, PhoneInviteDialogComponent],
  // Zoom's `video-player-container` is the SDK's own element, defined once the SDK loads.
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './compact-call-view.component.html',
  styleUrls: ['./compact-call-view.component.scss']
})
export class CompactCallViewComponent implements OnChanges, AfterViewInit, OnDestroy {
  /** How many thumbnails one page holds, by the card's size. */
  static readonly thumbnailsPerPage: Record<CompactCallSize, number> = {small: 3, large: 5, full: 8};

  /** The slot a shared screen is drawn in: the stage's own canvas, as Meet draws it (ITP-E-189-05). */
  static readonly shareSlot = 'share';

  readonly icons = COMPACT_CALL_ICONS;

  @Input({required: true}) view!: CompactCallViewModel;
  @Input() renderer: CompactCallRenderer | null = null;

  @Output() readonly toggleMute = new EventEmitter<void>();
  @Output() readonly toggleCamera = new EventEmitter<void>();
  @Output() readonly toggleShare = new EventEmitter<void>();
  @Output() readonly hangUp = new EventEmitter<void>();
  /** The strip's Expand, back to the card. */
  @Output() readonly expand = new EventEmitter<void>();
  @Output() readonly option = new EventEmitter<CompactCallOption>();

  /** The thumbnails' page on show. */
  thumbnails: CompactCallPage = {tiles: [], page: 0, pages: 0};
  menuOpen = false;
  confirmingEndForAll = false;
  panel: CompactCallPanel | null = null;

  private page = 0;
  private perPage = CompactCallViewComponent.thumbnailsPerPage.small;
  /** The mode last laid out: a panel does not outlive the card-to-strip switch (ITP-E-189-09). */
  private mode: CompactCallMode | null = null;
  /**
   * What the view drew, by slot ('stage', 'mini', 'share' or 'tile:<identity>'), through the reconcile Meet draws with too
   * (MEET-E-45-05). A slot whose video the renderer could not draw (ITP-E-189-02) — a camera on that shows nothing here, this
   * side's own the likeliest — keeps the initials, as Meet's tile falls back to the avatar, until a later layout draws it.
   */
  private readonly slots = new VideoSlotReconciler<string, CompactCallRenderer>({
    draw: CompactCallViewComponent.draw,
    drop: CompactCallViewComponent.drop
  });
  private readonly slotLayout: VideoSlotLayout<string, CompactCallRenderer> = {
    wanted: () => this.wantedSlots(),
    host: slot => this.hosts.get(slot),
    renderer: () => this.renderer
  };
  /** The slots there are now, by name; Angular creates and drops them with the layout. */
  private hosts = new Map<string, HTMLElement>();
  /** Slot changes run one after another: a draw still on its way never lands in a layout that moved on. */
  private staging: Promise<void> = Promise.resolve();
  private destroyed = false;
  private slotChanges: Subscription | null = null;

  /** Where the renderer may draw; `data-slot` names them. */
  @ViewChildren('videoSlot')
  private videoSlots!: QueryList<ElementRef<HTMLElement>>;

  /** The one share canvas, for the view's life (ITP-E-189-07); never under a structural directive, so it is there from the start. */
  @ViewChild('shareCanvas', {static: true})
  private shareCanvas!: ElementRef<HTMLCanvasElement>;

  constructor(private host: ElementRef<HTMLElement>) {
  }

  /** Whom the stage stands for, by name: the person, else the call. */
  get stageName(): string {
    return this.view.stage?.name || this.view.title;
  }

  get stageInitials(): string {
    return this.view.stage?.initials || CompactCallViewComponent.initials(this.view.title);
  }

  /** The strip's face: the initials of whom the call is with — the other person in a 1:1, the group's title in a group. */
  get faceInitials(): string {
    return CompactCallViewComponent.initials(this.view.title);
  }

  /** The face's carbon avatar pair, by the name's first letter. */
  get faceTone(): string {
    return CallParticipants.avatarTone(this.view.title);
  }

  /** What follows the timer under the strip's name: " · N in call", once anyone is. */
  get inCallLabel(): string {
    return CompactCallViewComponent.inCallLabel(this.view.inCall);
  }

  /** The strip's Open call stands for Open in Meet, so ⋯ offers it only on the card. */
  get openInMeetInMenu(): boolean {
    return this.view.options.openInMeet && this.view.mode === 'card';
  }

  get ariaLabel(): string {
    const call = this.view.video ? 'Video call' : 'Voice call';
    return this.view.mode === 'strip' && this.view.expandable ? `${call}, minimized` : call;
  }

  /** Whether `slot` shows initials: its person has no video, or it could not be drawn here (ITP-E-189-02). */
  showsInitials(slot: string, video: boolean | undefined): boolean {
    return !video || this.slots.isUndrawn(slot);
  }

  /** Add by phone is offered (ITP-E-198-01): this user may, and the call's account dials out. */
  get canAddPhone(): boolean {
    return !!this.view.options.addPhone && !!this.view.phone;
  }

  /** Share is a footer button on the card and a ⋯ item on the slim strip. */
  get shareInMenu(): boolean {
    return this.view.mode === 'strip' && this.view.self.canShare;
  }

  /** Whether someone else's screen is on the card's stage: the share canvas shows, else it is kept hidden. */
  get showsShare(): boolean {
    return this.view.mode === 'card' && !!this.view.sharer && !this.view.sharer.self;
  }

  ngOnChanges(): void {
    const perPage = CompactCallViewComponent.thumbnailsPerPage[this.view.size] ?? this.perPage;
    if (perPage !== this.perPage) {
      this.perPage = perPage;
      this.page = 0;
    }
    if (this.panel === 'participants' && !this.view.participants.length) {
      this.panel = null;
    }
    if (this.panel === 'phone' && !this.canAddPhone) {
      this.panel = null;
    }
    // A panel belongs to the mode it was opened in (ITP-E-189-09): the card minimized, or the strip expanded, closes it.
    if (this.panel && this.mode && this.view.mode !== this.mode) {
      this.panel = null;
    }
    this.mode = this.view.mode;
    this.layout();
  }

  ngAfterViewInit(): void {
    this.slotChanges = this.videoSlots.changes.subscribe(() => this.syncSlots());
    this.syncSlots();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.slotChanges?.unsubscribe();
    this.hosts.clear();
    this.sync();
  }

  onToggleMute(): void {
    this.toggleMute.emit();
  }

  onToggleCamera(): void {
    this.toggleCamera.emit();
  }

  onToggleShare(): void {
    this.closeMenu();
    this.toggleShare.emit();
  }

  onHangUp(): void {
    this.closeMenu();
    this.hangUp.emit();
  }

  onExpand(): void {
    this.closeMenu();
    this.expand.emit();
  }

  onToggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    this.confirmingEndForAll = false;
  }

  /** Participants and Devices open a panel above the controls; choosing the open one again closes it. */
  onPanel(panel: CompactCallPanel): void {
    this.closeMenu();
    this.panel = this.panel === panel ? null : panel;
    if (this.panel === 'devices') {
      this.option.emit({type: 'devices'});
    }
  }

  onClosePanel(): void {
    this.panel = null;
  }

  /** Add by phone's Call (ITP-E-198-01): the host dials; Participants shows where it stands, with Cancel. */
  onCallPhone(invite: CallPhoneInvite): void {
    this.option.emit({type: 'add-phone', invite});
    this.panel = 'participants';
  }

  /** A phone person's Cancel or Hang up (ITP-E-198-01). */
  onHangUpPhone(key: string): void {
    this.option.emit({type: 'hang-up-phone', key});
  }

  onRecord(): void {
    if (this.view?.options?.recordHeldBack) {
      return;
    }
    this.closeMenu();
    this.option.emit({type: 'record'});
  }

  /** Stop recording (ITP-E-189-06): the host's, in the card and the strip alike. */
  onStopRecording(): void {
    this.closeMenu();
    this.option.emit({type: 'stop-recording'});
  }

  /** Open in Meet ↗ and the strip's Open call: emitted inside the click, so the host's new window gets past a popup blocker. */
  onOpenInMeet(): void {
    this.closeMenu();
    this.option.emit({type: 'open-in-meet'});
  }

  onEndForAll(): void {
    this.confirmingEndForAll = true;
  }

  onConfirmEndForAll(): void {
    this.closeMenu();
    this.option.emit({type: 'end-for-all'});
  }

  onSelectMicrophone(deviceId: string): void {
    this.option.emit({type: 'microphone', deviceId});
  }

  onSelectSpeaker(deviceId: string): void {
    this.option.emit({type: 'speaker', deviceId});
  }

  onPage(step: number): void {
    this.page = this.thumbnails.page + step;
    this.layout();
  }

  closeMenu(): void {
    this.menuOpen = false;
    this.confirmingEndForAll = false;
  }

  trackPerson(_index: number, person: CompactCallPerson): string {
    return person.identity;
  }

  /**
   * A click outside closes what is open: the ⋯ menu, and the panel it opened (ITP-E-189-09) — on the stage, the controls
   * or the page, as Meet's side panels and WhatsApp's sheets go away. The ⋯ button and its menu are excepted for both, so
   * a second press of ⋯ toggles the menu over the panel instead of closing it, and choosing the item that opened the
   * panel toggles it ({@link onPanel}); a click inside the panel — a device, a row — keeps it, since people compare.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    const options = this.host.nativeElement.querySelector('.compact-call-view-options');
    const inOptions = !!target && !!options?.contains(target);
    if (this.menuOpen && !inOptions) {
      this.closeMenu();
    }
    // What was clicked is gone from the page already: one of this view's own, re-rendered by the click — not outside.
    if (this.panel && !inOptions && target?.isConnected && !this.panelElement?.contains(target)) {
      this.panel = null;
    }
  }

  /** The open panel's element, in the card or floating over the strip. */
  private get panelElement(): Element | null {
    return this.host.nativeElement.querySelector('.compact-call-view-panel');
  }

  /** Esc closes the menu, then the panel. */
  @HostListener('keydown.escape')
  onEscape(): void {
    if (this.menuOpen) {
      this.closeMenu();
    } else {
      this.panel = null;
    }
  }

  /** The thumbnails as they stand, then the videos where the layout puts them. */
  private layout(): void {
    const view = this.view;
    // While someone shares, the screen has the big space and every speaker is a thumbnail.
    const stage = view.mode === 'card' && !view.sharer ? view.stage?.identity ?? null : null;
    const tiles = view.mode === 'card' ? CompactCallViewComponent.others(view.people, stage) : [];
    this.thumbnails = CompactCallViewComponent.pageOf(tiles, this.page, this.perPage);
    this.page = this.thumbnails.page;
    this.sync();
  }

  /** The slots Angular has now — and the share canvas, always (ITP-E-189-07) — then the videos that belong in them. */
  private syncSlots(): void {
    this.hosts = new Map((this.videoSlots?.toArray() ?? [])
      .map(slot => [slot.nativeElement.getAttribute('data-slot') ?? '', slot.nativeElement] as [string, HTMLElement]));
    if (this.shareCanvas?.nativeElement) {
      this.hosts.set(CompactCallViewComponent.shareSlot, this.shareCanvas.nativeElement);
    }
    this.sync();
  }

  private sync(): void {
    this.staging = this.staging.then(() => this.slots.sync(this.slotLayout))
      .catch(error => console.warn('[calls] the call view\'s video failed', error));
  }

  /** Whose video goes in which slot now: the speaker on the stage (or in the strip), a shared screen, this page's thumbnails. */
  private wantedSlots(): Map<string, string> {
    const wanted = new Map<string, string>();
    const view = this.view;
    if (this.destroyed || !view) {
      return wanted;
    }
    // Someone else's screen stays drawn in the one canvas through a strip spell (ITP-E-189-07): the view is theirs again on Expand.
    if (view.sharer && !view.sharer.self) {
      wanted.set(CompactCallViewComponent.shareSlot, view.sharer.identity);
    }
    if (view.mode === 'strip') {
      if (view.video && view.stage?.video) {
        wanted.set('mini', view.stage.identity);
      }
      return wanted;
    }
    if (!view.sharer && view.stage?.video) {
      wanted.set('stage', view.stage.identity);
    }
    this.thumbnails.tiles.filter(tile => tile.video)
      .forEach(tile => wanted.set(CompactCallViewComponent.tileSlot(tile.identity), tile.identity));
    return wanted;
  }

  /** A slot's video drawn: the stage large, every thumbnail small, a shared screen in its own slot. */
  private static draw(renderer: CompactCallRenderer, slot: string, identity: string, host: HTMLElement): Promise<boolean> {
    return slot === CompactCallViewComponent.shareSlot
      ? renderer.attachShare(host, identity)
      : renderer.attachVideo(identity, host, slot === 'stage' ? 'large' : 'small');
  }

  private static drop(renderer: CompactCallRenderer, slot: string, identity: string, host: HTMLElement): Promise<void> {
    return slot === CompactCallViewComponent.shareSlot ? renderer.detachShare(host) : renderer.detachVideo(identity, host);
  }

  /** Everyone but whoever the stage stands for, once each, this side last. */
  static others(people: CompactCallPerson[], stage: string | null): CompactCallPerson[] {
    const unique = people.filter((person, i) =>
      person.identity && person.identity !== stage && people.findIndex(other => other.identity === person.identity) === i);
    return [...unique.filter(person => !person.self), ...unique.filter(person => person.self)];
  }

  /** One page of `tiles`, `perPage` at a time; a page past the last (someone left) shows the last. */
  static pageOf(tiles: CompactCallPerson[], page: number, perPage: number): CompactCallPage {
    const pages = Math.ceil(tiles.length / perPage);
    const shown = Math.max(0, Math.min(page, pages - 1));
    return {tiles: tiles.slice(shown * perPage, (shown + 1) * perPage), page: shown, pages};
  }

  static tileSlot(identity: string): string {
    return `tile:${identity}`;
  }

  /** " · N in call" for `count` people in the call; empty while nobody is counted yet. */
  static inCallLabel(count: number): string {
    return count > 0 ? ` · ${count} in call` : '';
  }

  static initials(name: string): string {
    const words = (name || '').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
    return words.slice(0, 2).map(word => word[0].toUpperCase()).join('') || '?';
  }
}
